import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, FileText, CheckSquare, UserPlus, GraduationCap, ShieldCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <Card className="hover:shadow-md transition-shadow" data-testid={testid}>
    <CardContent className="pt-6 text-center">
      <Icon className={`h-6 w-6 mx-auto mb-2 ${accent}`} />
      <p className="font-mono text-3xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </CardContent>
  </Card>
);

const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "-";
  }
};

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/stats")
      .then((res) => setStats(res.data))
      .catch(() => toast.error("Não foi possível carregar as estatísticas"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!stats) return null;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="admin-page">
      <div>
        <div className="flex items-center gap-2 text-primary mb-1">
          <ShieldCheck className="h-5 w-5" />
          <span className="text-sm font-medium">Administração</span>
        </div>
        <h1 className="font-heading font-bold text-3xl tracking-tight">Estatísticas da Plataforma</h1>
        <p className="text-muted-foreground mt-1 text-sm">Adesões e utilização em tempo real.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard icon={Users} label="Professores registados" value={stats.total_users} accent="text-primary" testid="stat-total-users" />
        <StatCard icon={UserPlus} label="Novos (últimos 7 dias)" value={stats.new_users_7d} accent="text-green-600" testid="stat-new-7d" />
        <StatCard icon={UserPlus} label="Novos (últimos 30 dias)" value={stats.new_users_30d} accent="text-blue-600" testid="stat-new-30d" />
        <StatCard icon={FileText} label="Testes criados" value={stats.total_sessions} accent="text-amber-500" testid="stat-sessions" />
        <StatCard icon={GraduationCap} label="Testes de alunos" value={stats.total_students} accent="text-purple-600" testid="stat-students" />
        <StatCard icon={CheckSquare} label="Correções feitas" value={stats.total_corrections} accent="text-teal-600" testid="stat-corrections" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading">Registos mais recentes</CardTitle>
        </CardHeader>
        <CardContent>
          {stats.recent_users?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-right">Data de registo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.recent_users.map((u, i) => (
                  <TableRow key={u.email || i} data-testid={`recent-user-${i}`}>
                    <TableCell className="font-medium">{u.name || "-"}</TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{fmtDate(u.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">Ainda não há registos.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
